import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { swapViewColumn } from "@design-system/ViewTable/swapViewColumn";
import { SplitParentTransactionColumn } from "./splitParentTransactionsViewConfig";
import { SplitVersionColumn } from "./splitVersionsViewConfig";
import { SplitDetailColumn } from "./splitDetailsViewConfig";

export const createDefaultSplitParentTransactionsView = (
    isGlFirst: boolean,
): View => ({
    type: ViewType.HISTORY_SPLIT_PARENT_TRANSACTION,
    columns: [
        { id: SplitParentTransactionColumn.DATE },
        { id: SplitParentTransactionColumn.DESCRIPTION },
        {
            id: isGlFirst
                ? SplitParentTransactionColumn.LEDGER
                : SplitParentTransactionColumn.CATEGORY,
        },
        { id: SplitParentTransactionColumn.AMOUNT },
        {
            id: SplitParentTransactionColumn.ENTITY,
            options: { pin: "right" },
        },
        {
            id: SplitParentTransactionColumn.ACTIONS,
            options: { pin: "right" },
        },
    ],
    filters: {},
    options: {
        selection: true,
        density: ViewDensity.MD,
    },
});

export const createDefaultSplitVersionsView = (isGlFirst: boolean): View => ({
    type: ViewType.HISTORY_SPLIT_VERSIONS,
    columns: [
        { id: SplitVersionColumn.DATE },
        { id: SplitVersionColumn.SPLITS },
        { id: SplitVersionColumn.DESCRIPTION },
        {
            id: isGlFirst
                ? SplitVersionColumn.LEDGER
                : SplitVersionColumn.CATEGORY,
        },
        { id: SplitVersionColumn.AMOUNT },
        { id: SplitVersionColumn.ENTITY, options: { pin: "right" } },
        { id: SplitVersionColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: true,
        density: ViewDensity.MD,
    },
});

export const createDefaultSplitDetailsView = (isGlFirst: boolean): View => ({
    type: ViewType.HISTORY_SPLIT_DETAILS,
    columns: [
        { id: SplitDetailColumn.DATE },
        { id: SplitDetailColumn.DESCRIPTION },
        {
            id: isGlFirst
                ? SplitDetailColumn.LEDGER
                : SplitDetailColumn.CATEGORY,
        },
        { id: SplitDetailColumn.AMOUNT },
        { id: SplitDetailColumn.ACCOUNT },
        { id: SplitDetailColumn.ENTITY, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: true,
        density: ViewDensity.MD,
    },
});

/**
 * Swaps the Category and Cash-ledger columns of a split view to match the
 * workspace's GL-first setting. `useIsGlFirst` defaults to GL-first while the
 * workspace loads, so the initial view state can disagree with the resolved
 * value; normalizing on every render keeps the rendered column in sync.
 *
 * The CATEGORY/LEDGER column ids share the same string values across all three
 * split column enums, so a single helper covers every split view.
 */
export const withGlFirstSplitLedgerColumn = (
    view: View,
    isGlFirst: boolean,
): View => {
    const fromColumnId = isGlFirst
        ? SplitParentTransactionColumn.CATEGORY
        : SplitParentTransactionColumn.LEDGER;
    const toColumnId = isGlFirst
        ? SplitParentTransactionColumn.LEDGER
        : SplitParentTransactionColumn.CATEGORY;

    return {
        ...view,
        columns: swapViewColumn(view.columns, {
            fromColumnId,
            toColumnId,
            createTargetColumn: (column) => ({ ...column, id: toColumnId }),
        }),
    };
};
